import React, { FC, useEffect } from 'react';
import {
  GoogleMap,
  StandaloneSearchBox,
  useJsApiLoader,
  Marker,
} from '@react-google-maps/api';
import { useState } from 'react';
import styles from './map.module.scss';
import InputField from '../InputField';
import { useField } from 'formik';
import LoaderAnimation from '../LoaderAnimation';

const containerStyle = {
  width: '100%',
  height: '200px',
  borderRadius: '8px',
};

const defaultCenter = {
  lat: 54.1475046394639,
  lng: -104.78574084619436,
};

interface Location {
  lat: number;
  lng: number;
}

interface MapProps {
  setAddress: (
    addressComponents: google.maps.GeocoderAddressComponent[],
    latling: google.maps.LatLng | null
  ) => void;
  name: string;
  latitude?: string;
  longitude?: string;
  info?: boolean;
  infoClick?: () => void;
}

const Map: FC<MapProps> = props => {
  const { setAddress, name, latitude, longitude, info,
    infoClick } = props;

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '',
    libraries: ['places'],
  });

  const options = {
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
  };

  const [_field, _meta, helpers] = useField(name);

  let center = defaultCenter;
  if (latitude && longitude) {
    center = {
      lat: parseFloat(latitude),
      lng: parseFloat(longitude),
    };
  }

  const [selectedLocation, setSelectedLocation] = useState<Location>(center);
  const [searchBox, setSearchBox] = useState<google.maps.places.SearchBox | null>(null);

  useEffect(() => {
    if (latitude && longitude)
      setSelectedLocation({
        lat: parseFloat(latitude),
        lng: parseFloat(longitude),
      });
  }, [latitude, longitude]);

  const handleSearch = (searchText: string) => {
    if (searchText.trim() === '') {
      return;
    }

    const geocoder = new window.google.maps.Geocoder();
    console.log('searchText is ', searchText);
    geocoder.geocode({ address: searchText }, (results, status) => {
      if (status === 'OK' && results!.length > 0 && results![0]) {
        const { lat, lng } = results![0].geometry.location;
        setSelectedLocation({ lat: lat(), lng: lng() });
        const addressComponents = results![0].address_components;
        setDetailsFromAddressComponent(
          addressComponents,
          results![0].geometry.location
        );
      } else {
        console.error(
          'Geocode was not successful for the following reason:',
          status
        );
        // Handle geocoding errors
      }
    });
  };

  const onSBLoad = (ref: google.maps.places.SearchBox) => {
    setSearchBox(ref);
  };

  const setDetailsFromAddressComponent = (
    addressComponents: google.maps.GeocoderAddressComponent[],
    location: google.maps.LatLng | null
  ) => {
    setAddress(addressComponents, location);
  };

  return isLoaded ? (
    <>
      <div className={styles.searchBox}>
        <StandaloneSearchBox
          onLoad={onSBLoad}
          onPlacesChanged={() => {
            const places = searchBox?.getPlaces();
            if (places && places[0] && places[0].formatted_address) {
              helpers.setValue(places[0].formatted_address);
              handleSearch(places[0].formatted_address);
            }
          }}>
          <div>
            <InputField
              name={name}
              placeholderstring={'Search for Location'}
              label="Search for Location"
              info={info}
              infoClick={infoClick}
            />
          </div>
        </StandaloneSearchBox>
      </div>
      <div>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={selectedLocation}
          zoom={6}
          options={options}>
          {selectedLocation && (
            <Marker
              position={selectedLocation}
              draggable={true}
              onDragEnd={(e: google.maps.MapMouseEvent) => {
                if (!e.latLng) return;
                const newPosition: Location = {
                  lat: e.latLng.lat(),
                  lng: e.latLng.lng(),
                };
                setSelectedLocation(newPosition);
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ location: e.latLng }, (results, status) => {
                  if (status === 'OK' && results![0]) {
                    const findPlaceWithCountry = results?.find(item =>
                      item.address_components.find(item =>
                        item.types.includes('country')
                      )
                    );
                    helpers.setValue(findPlaceWithCountry?.formatted_address);
                    setDetailsFromAddressComponent(
                      findPlaceWithCountry?.address_components ?? [],
                      e.latLng
                    );
                  } else {
                    console.error(
                      'Geocode was not successful for the following reason:',
                      status
                    );
                  }
                });
              }}
            />
          )}
        </GoogleMap>
      </div>
    </>
  ) : (
    <div className={styles.loaderAnimation}>
      <LoaderAnimation />
    </div>
  );
};

export default Map;
